<template>
  <v-dialog
    v-model="dialog"
    max-width="1000px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            text
            icon
            color="info"
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon>
              mdi-information-outline
            </v-icon>
          </v-btn>
        </template>
        <span>View Taken Applications</span>
      </v-tooltip>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="View Taken Leave Applications"
      :text="entitlement.employee.person.name + ' ('+ entitlement.leave_type.name +')'"
      class="mb-0"
      max-height="750px"
    >
      <v-container>
        <setup-leaves-leave-entitlement-dialog-create-leave-applications
          v-if="$hasPermissionTo(['create leaves on behalf']) && (entitlement.end_date && entitlement.end_date < $moment().startOf('year').format('YYYY-MM-DD'))"
          :entitlement="entitlement"
          :selected-year="selectedYear"
          @created="refreshData"
        />
        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="leaveApplications"
          class="overflow"
        >
          <template v-slot:item.requested_days="{ item }">
            <span>{{ item.requested_days }}
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div
                  v-for="item in item.leave_days"
                  :key="item.leave_session.id"
                  class="font-weight-bold text-left"
                >
                  {{ $moment(item.date).format('YYYY-MM-DD') }} ({{ $moment(item.date).format('ddd') }}) - {{ item.leave_session.name }}
                </div> 
              </v-tooltip>              
            </span>
          </template>

          <template v-slot:item.latest_status.name="{ item }">
            <span
              :class="{ 
                'warning--text': (item.latest_status.id === 100),
                'red--text': (item.latest_status.id === 500), 
                'grey--text': (item.latest_status.id === 501), 
                'green--text': (item.latest_status.id === 200) 
              }"
            >
              {{ item.latest_status.name }}
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="item.latest_status.id === 500 || item.latest_status.id === 501 || item.latest_status.id === 200"
                    :class="{
                      'red--text': (item.latest_status.id === 500), 
                      'grey--text': (item.latest_status.id === 501),
                      'green--text': (item.latest_status.id === 200)
                    }"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span class="font-weight-bold">Approver:</span> {{ item.latest_approver }}
                <br>
                <span class="font-weight-bold">Date:</span> {{ $moment(item.latest_approval_date).format('YYYY-MM-DD hh:mm A') }}
                <br>
                <span class="font-weight-bold">Remark:</span> {{ item.latest_remark }}
                <br>
                <span
                  v-if="item.latest_company_employee_additional_doe_payroll"
                  class="font-weight-bold"
                >
                  Payroll:
                </span> 
                {{ item.latest_company_employee_additional_doe_remark ? item.latest_company_employee_additional_doe_remark + ' on ' + item.latest_company_employee_additional_doe_payroll + ' payroll' : item.latest_company_employee_additional_doe_payroll }}
              </v-tooltip>
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <setup-leaves-leave-entitlement-dialog-cancel-leave-applications
              v-if="(![100,500,501].includes(item.latest_status.id) || pendingLeavePassCancellationDate(item)) && $hasPermissionTo(['cancel leave applications'])"
              :application="item"
              @cancel="refreshData"
            />
          </template>
        </v-data-table>
      </v-container>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      SetupLeavesLeaveEntitlementDialogCancelLeaveApplications: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogCancelLeaveApplications'),
      SetupLeavesLeaveEntitlementDialogCreateLeaveApplications: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogCreateLeaveApplications'),
    },
    props: {
      entitlement: {
        type: Object,
        default: null,
      },    
      selectedYear: {
        type: String,
        required: true,
      }, 
    },
    data () {
      return {
        dialog: false,
        loading: false,
        headers: [
          {
            text: 'Type',
            align: 'left',
            value: 'leave_entitlement.leave_type.name',
          },
          {
            text: 'Start Date',
            align: 'left',
            value: 'start_date',
          },
          {
            text: 'End Date',
            align: 'left',
            value: 'end_date',
          },
          {
            text: 'Days',
            align: 'left',
            value: 'requested_days',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'latest_status.name',
          },
          {
            text: '',
            align: 'center',
            sortable: false,
            value: 'action',
          },
        ],
        leaveApplications: [],
      }
    },

    watch: {
      dialog: function (value) {
        if (value) {
          this.loadLeaveApplications()
        }
      },
    },

    methods: {
      loadLeaveApplications () {
        this.loading = true

        this.$api.entitlementLeaveApplications().list(this.entitlement.id)
          .then(res => {
            this.leaveApplications = []
            res.data.map(application => {
              let approvals = ((application.approval_request || {}).approvals || [])
              let companyEmployeeAdditionalDoes = ((application.company_employee_additional_does || {}) || [])
            
              const sortedApprovals = _.orderBy(approvals, ['created_at'], ['asc'])
              const latestApproval = _.orderBy(approvals, ['created_at'], ['desc'])[0]
              const latestCompanyEmployeeAdditionalDoe = _.orderBy(companyEmployeeAdditionalDoes, ['updated_at'], ['desc'])[0]

              this.leaveApplications.push({ 
                ...application, 
                latest_status: latestApproval.status, 
                latest_remark: latestApproval.remark, 
                latest_approval_date: latestApproval.updated_at, 
                latest_approver: latestApproval.approver ? ((latestApproval.approver || {}).person || {}).name : latestApproval.approver_name,
                latest_company_employee_additional_doe_payroll: (latestCompanyEmployeeAdditionalDoe || {}).payroll_period ? this.$moment(latestCompanyEmployeeAdditionalDoe.payroll_period.payroll_date).format('MMM YYYY') : null,
                latest_company_employee_additional_doe_remark: latestCompanyEmployeeAdditionalDoe ? latestCompanyEmployeeAdditionalDoe.remark : null,
                approvals: sortedApprovals, 
              })
            })

            this.leaveApplications = _.orderBy(this.leaveApplications, ['latest_status.id', 'created_at'], ['asc', 'desc'])
            this.loading = false
          })
      },

      refreshData () {
        this.loadLeaveApplications()
        this.$emit('cancel')
        console.clear()
      },

      pendingLeavePassCancellationDate (item) {
        if (item.latest_status.id === 100 && item.start_date <= this.$moment().format('YYYY-MM-DD')) {
          return true
        }
        return false
      },
    },
  }
</script>

<style lang="css" scoped>
  .overflow {
    max-height:600px;
    overflow: auto;
  }
</style>
